import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useContent } from '../contexts/ContentContext';

interface FAQItem {
  question: string;
  answer: string;
}

export const FAQ = () => {
  const { t } = useTranslation();
  const { getValue } = useContent();
  const baseItems = t('faq.items', { returnObjects: true }) as FAQItem[];
  const items = baseItems.map((item, idx) => ({
    question: getValue(`faq.items.${idx}.question`, item.question),
    answer: getValue(`faq.items.${idx}.answer`, item.answer),
  }));
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const eyebrow = getValue('faq.eyebrow', t('faq.eyebrow'));
  const title = getValue('faq.title', t('faq.title'));

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-14 bg-regai-surface-alt animate-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="uppercase tracking-widest text-xs font-semibold text-regai mb-2">
            {eyebrow}
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold leading-tight">{title}</h2>
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-6">
          {items.map((item, idx) => (
            <div
              key={idx}
              className="rounded-2xl border border-regai bg-regai-surface p-6"
            >
              <button
                onClick={() => toggle(idx)}
                className="w-full text-left flex justify-between items-start gap-4"
                aria-expanded={openIndex === idx}
                aria-controls={`faq-answer-${idx}`}
              >
                <h3 className="font-bold">{item.question}</h3>
                <svg
                  className={`w-5 h-5 flex-none transition-transform ${
                    openIndex === idx ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {openIndex === idx && (
                <p
                  id={`faq-answer-${idx}`}
                  className="text-regai-muted mt-3"
                  role="region"
                >
                  {item.answer}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
